// engine-exception: auth/onboarding flow, not a resource screen — stays hand-written (blueprint §1: web/src/pages is legacy)
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getSupabase, type OAuthProvider as Provider } from '../lib/supabase';
import { runtimeConfig } from '../lib/runtimeConfig';
import { federatedSession, type SignInRoute } from '../lib/federatedSession';
import { AuthCard, AuthScene, ProviderMark } from '../components/authui';
import { INVITE_TOKEN_KEY, invitePath, useInvitePreview } from '../lib/invite';

// Sign in is EMAIL FIRST: the email's domain decides the route. An organisation that has verified
// the domain and signs in with its HaloPSA gets "Sign in with SSO" (Halo is the identity provider;
// no Supabase involvement), with "Sign in with a password instead" as the fallback; every other
// email goes straight to the password step.
//
// Supabase Auth (the password route). Email/password + a 6-digit email CODE (scanner-proof, works cross-device) +
// OAuth/OIDC (from AUTH_PROVIDERS). A segmented Sign in / Create account switch drives the
// same form. On success, AuthProvider picks up the session and we redirect home (the
// OnboardingGate then routes new users through founding an org).
//
// Why codes, not magic links: corporate mail scanners (Safe Links, Proofpoint, Mimecast)
// pre-fetch links and burn the one-time token before the user clicks. A typed code can't be
// "clicked" by a scanner, and it works when the mail is opened on a different device.

const OAUTH: Record<string, { provider: Provider; label: string; mark?: string }> = {
  google: { provider: 'google', label: 'Google', mark: 'google' },
  azure: { provider: 'azure', label: 'Microsoft', mark: 'azure' },
  microsoft: { provider: 'azure', label: 'Microsoft', mark: 'azure' },
  github: { provider: 'github', label: 'GitHub' },
  apple: { provider: 'apple', label: 'Apple' },
};

export function LoginPage() {
  const { session, configured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // /login?invite=<token>&mode=signup|signin: an invitee creating (or signing in to) the account
  // for an organisation that already exists — the email is the invited one and is not editable,
  // there is no org founding, and every way back (session, confirm email) returns to the invite.
  const search = new URLSearchParams(location.search);
  const inviteToken = search.get('invite');
  const invite = useInvitePreview(inviteToken).data ?? null;
  const [mode, setMode] = useState<'signin' | 'signup'>(search.get('mode') === 'signup' ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  // The sign-in steps: the email, then SSO (when the email's organisation has it) or the password.
  const [step, setStep] = useState<'email' | 'sso' | 'password'>('email');
  const [route, setRoute] = useState<SignInRoute | null>(null);
  useEffect(() => {
    if (!invite) return;
    setEmail(invite.email);
    const sso = invite.sign_in === 'halo';
    setRoute({ sso, tenantId: invite.tenant_id, organisation: invite.organisation });
    setStep(sso ? 'sso' : 'password');
  }, [invite]);
  useEffect(() => {
    if (inviteToken) localStorage.setItem(INVITE_TOKEN_KEY, inviteToken);
  }, [inviteToken]);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The email-code sub-flow. otpKind distinguishes a passwordless sign-in code, a
  // password-recovery code (which lands on the set-new-password screen), and a
  // confirm-signup code (sent right after creating an account with confirm-email on).
  const [otp, setOtp] = useState<{ kind: 'signin' | 'recovery' | 'signup' } | null>(null);
  const [code, setCode] = useState('');
  const isSignup = mode === 'signup';

  useEffect(() => {
    if (session) navigate(inviteToken ? invitePath(inviteToken) : '/', { replace: true });
  }, [session, navigate, inviteToken]);

  // Back from the organisation's Halo (/login/halo/callback?code&state): finish server-side.
  const haloReturn = location.pathname === '/login/halo/callback';
  useEffect(() => {
    if (!haloReturn) return;
    const q = new URLSearchParams(location.search);
    const code = q.get('code');
    const state = q.get('state');
    if (q.get('error') || !code || !state) {
      setError(q.get('error_description') ?? 'Halo did not complete the sign-in. Please try again.');
      navigate('/login', { replace: true });
      return;
    }
    void run('halo', async () => {
      await federatedSession.complete(code, state);
      navigate('/', { replace: true });
    }).then(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once for this callback URL
  }, [haloReturn]);

  // Email links (confirm, magic) return to the invite when there is one, so confirming on another
  // device still lands on the invite instead of org founding.
  const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
  const redirectTo = origin && inviteToken ? `${origin}${invitePath(inviteToken)}` : origin;

  async function run(tag: string, fn: () => Promise<void>) {
    setBusy(tag);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(null);
    }
  }

  const submitPassword = () =>
    run('password', async () => {
      const supa = getSupabase();
      if (isSignup) {
        const { data, error } = await supa.auth.signUp({ email, password, options: { emailRedirectTo: redirectTo } });
        if (error) throw error;
        // Confirm-email is on: the confirm-signup template carries a 6-digit code —
        // collect it right here instead of bouncing the user out to their inbox and back.
        if (!data.session) {
          setCode('');
          setOtp({ kind: 'signup' });
          setNotice(`Almost there — we emailed a 6-digit confirmation code to ${email}.`);
        }
      } else {
        const { error } = await supa.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    });

  // Passwordless: email a 6-digit sign-in code, then verify it below.
  const sendSignInCode = () =>
    run('code', async () => {
      if (!email) throw new Error('Enter your email first.');
      const { error } = await getSupabase().auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
      });
      if (error) throw error;
      setCode('');
      setOtp({ kind: 'signin' });
      setNotice(`We emailed a 6-digit code to ${email}. Enter it below.`);
    });

  // Forgot password: email a recovery code; verifying it drops the user on /reset to set a new one.
  const sendRecoveryCode = () =>
    run('forgot', async () => {
      if (!email) throw new Error('Enter your email first, then tap “Forgot password”.');
      const { error } = await getSupabase().auth.resetPasswordForEmail(email, {
        redirectTo: origin ? `${origin}/reset` : undefined,
      });
      if (error) throw error;
      setCode('');
      setOtp({ kind: 'recovery' });
      setNotice(`We emailed a password-reset code to ${email}. Enter it below.`);
    });

  // Confirm-signup: resend the confirmation email (a fresh 6-digit code).
  const resendSignupCode = () =>
    run('resend-signup', async () => {
      const { error } = await getSupabase().auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: redirectTo },
      });
      if (error) throw error;
      setNotice(`We emailed a new confirmation code to ${email}.`);
    });

  const verifyCode = () =>
    run('verify', async () => {
      const token = code.replace(/\s/g, '');
      if (!token) throw new Error('Enter the code from your email.');
      const kind = otp?.kind ?? 'signin';
      const { error } = await getSupabase().auth.verifyOtp({
        email,
        token,
        type: kind === 'recovery' ? 'recovery' : kind === 'signup' ? 'signup' : 'email',
      });
      if (error) throw error;
      // Session is now set. A recovery code lands on the set-new-password screen; a sign-in
      // or confirm-signup code lets the session effect redirect home (the OnboardingGate
      // then routes brand-new users through founding an org).
      if (kind === 'recovery') navigate('/reset', { replace: true });
    });

  const oauth = (provider: Provider, tag: string) =>
    run(tag, async () => {
      const { error } = await getSupabase().auth.signInWithOAuth({ provider, options: { redirectTo } });
      if (error) throw error;
    });

  // Step 1: the email decides the route (SSO for an organisation that signs in with its Halo).
  const continueWithEmail = () =>
    run('lookup', async () => {
      if (!email.includes('@')) throw new Error('Enter your work email.');
      const r = await federatedSession.signInRouteFor(email);
      setRoute(r);
      setPassword('');
      setStep(r.sso ? 'sso' : 'password');
    });

  const changeEmail = () => {
    setStep('email');
    setRoute(null);
    setPassword('');
    setError(null);
    setNotice(null);
  };

  // SSO: organisations whose NovaPSA sits on top of their HaloPSA sign in with Halo — Halo is the
  // identity provider and the session it grants is what NovaPSA uses to read and write their data.
  const halo = () =>
    run('halo', async () => {
      if (!route?.tenantId) throw new Error('Your organisation does not use single sign-on. Sign in with your password.');
      await federatedSession.begin(route.tenantId);
    });

  const sso = () =>
    run('sso', async () => {
      const domain = email.includes('@') ? email.split('@')[1] : email;
      if (!domain) throw new Error('Enter your work email or domain.');
      const { data, error } = await getSupabase().auth.signInWithSSO({ domain });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    });

  if (haloReturn) {
    return (
      <AuthScene>
        <AuthCard title="Signing you in" subtitle="Finishing your Halo sign-in…">
          {error && <div className="auth-msg err">⚠ {error}</div>}
        </AuthCard>
      </AuthScene>
    );
  }

  const emailLine = (
    <p className="auth-alt" style={{ marginTop: 0 }}>
      <strong>{email}</strong>{' '}
      {!invite && (
        <button type="button" className="linklike sm" onClick={changeEmail}>
          Use a different email
        </button>
      )}
    </p>
  );

  // ── Step 1 (sign in): the email ──
  if (!isSignup && !otp && step === 'email') {
    return (
      <AuthScene>
        <AuthCard>
          <div className="auth-head">
            <h1>Welcome back</h1>
            <p>Enter your work email to sign in.</p>
          </div>
          <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void continueWithEmail(); }}>
            <div className="field">
              <label>Work email</label>
              <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoFocus required />
            </div>
            <button type="submit" className="btn primary block lift" disabled={busy !== null}>
              {busy === 'lookup' ? 'Checking…' : 'Continue'}
            </button>
            {error && <div className="auth-msg err">⚠ {error}</div>}
          </form>
          {configured && (
            <p className="auth-alt">
              New to NovaPSA?{' '}
              <button type="button" className="linklike" onClick={() => { setMode('signup'); setError(null); setNotice(null); }}>
                Create one
              </button>
            </p>
          )}
        </AuthCard>
      </AuthScene>
    );
  }

  // ── Step 2a (sign in): single sign-on through the organisation's Halo ──
  if (!isSignup && !otp && step === 'sso') {
    return (
      <AuthScene>
        <AuthCard>
          <div className="auth-head">
            <h1>{route?.organisation ? `Sign in to ${route.organisation}` : 'Sign in'}</h1>
            <p>Your organisation signs in with single sign-on.</p>
          </div>
          <div className="auth-form">
            {emailLine}
            <button type="button" className="btn primary block lift" onClick={() => void halo()} disabled={busy !== null} autoFocus>
              {busy === 'halo' ? 'Opening sign-in…' : 'Sign in with SSO'}
            </button>
            {configured && (
              <button type="button" className="btn ghost sm" onClick={() => { setStep('password'); setError(null); }} disabled={busy !== null}>
                Sign in with a password instead
              </button>
            )}
            {error && <div className="auth-msg err">⚠ {error}</div>}
          </div>
        </AuthCard>
      </AuthScene>
    );
  }

  if (!configured) {
    return (
      <AuthScene>
        <AuthCard title="Password sign-in is not available" subtitle={email ? `There is no single sign-on for ${email}.` : undefined}>
          <p className="auth-msg info">
            Set <code>SUPABASE_URL</code> and <code>SUPABASE_ANON_KEY</code> on the web service to enable password sign-in.
          </p>
          <p className="auth-alt">
            <button type="button" className="linklike" onClick={changeEmail}>← Use a different email</button>
          </p>
        </AuthCard>
      </AuthScene>
    );
  }

  // ── Code-entry view (passwordless sign-in, password recovery, confirm-signup) ──
  if (otp) {
    const recovery = otp.kind === 'recovery';
    const confirming = otp.kind === 'signup';
    const resend = recovery ? sendRecoveryCode : confirming ? resendSignupCode : sendSignInCode;
    return (
      <AuthScene>
        <AuthCard>
          <div className="auth-head">
            <h1>{recovery ? 'Reset your password' : confirming ? 'Confirm your email' : 'Enter your code'}</h1>
            <p>
              We emailed a 6-digit code to <strong>{email}</strong>. It expires shortly — enter it to{' '}
              {recovery ? 'set a new password' : confirming ? 'activate your account' : 'sign in'}.
            </p>
          </div>
          <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void verifyCode(); }}>
            <div className="field">
              <label>6-digit code</label>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                autoFocus
                required
                style={{ letterSpacing: '0.4em', fontSize: 'var(--fs-20)', textAlign: 'center' }}
              />
            </div>
            <button type="submit" className="btn primary block lift" disabled={busy !== null}>
              {busy === 'verify' ? 'Verifying…' : recovery ? 'Continue' : confirming ? 'Confirm & continue' : 'Sign in'}
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => void resend()}
              disabled={busy !== null}
            >
              {busy === 'code' || busy === 'forgot' || busy === 'resend-signup' ? 'Resending…' : 'Resend code'}
            </button>
            {error && <div className="auth-msg err">⚠ {error}</div>}
            {notice && <div className="auth-msg ok">✓ {notice}</div>}
          </form>
          <p className="auth-alt">
            <button type="button" className="linklike" onClick={() => { setOtp(null); setError(null); setNotice(null); }}>
              ← Back to sign in
            </button>
          </p>
        </AuthCard>
      </AuthScene>
    );
  }

  const providers = runtimeConfig.authProviders;
  const oauthProviders = providers.map((p) => OAUTH[p]).filter((o): o is (typeof OAUTH)[string] => Boolean(o));
  const ssoEnabled = providers.includes('sso') || providers.includes('oidc');

  return (
    <AuthScene>
      <AuthCard>
        <div className="auth-head">
          {invite ? (
            <>
              <h1>{isSignup ? `Join ${invite.organisation}` : `Sign in to join ${invite.organisation}`}</h1>
              <p>{isSignup ? `Create your account for ${invite.email}.` : `Sign in as ${invite.email} to accept your invite.`}</p>
            </>
          ) : (
            <>
              <h1>{isSignup ? 'Create your organization' : 'Welcome back'}</h1>
              <p>{isSignup ? 'Start with your work email — you’ll set up your company next.' : 'Sign in to your finance workspace.'}</p>
            </>
          )}
        </div>

        <div className="auth-tabs" data-active={mode}>
          <button type="button" className={mode === 'signin' ? 'active' : ''} onClick={() => { setMode('signin'); if (!invite) setStep('email'); setError(null); setNotice(null); }}>
            Sign in
          </button>
          <button type="button" className={mode === 'signup' ? 'active' : ''} onClick={() => { setMode('signup'); setError(null); setNotice(null); }}>
            Create account
          </button>
        </div>

        <form className="auth-form" onSubmit={(e) => { e.preventDefault(); void submitPassword(); }}>
          <div className="field">
            <label>Work email</label>
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required readOnly={Boolean(invite) || !isSignup} aria-readonly={Boolean(invite) || !isSignup} />
            {!isSignup && !invite && (
              <button type="button" className="linklike sm" onClick={changeEmail}>
                Use a different email
              </button>
            )}
          </div>
          <div className="field">
            <div className="field-row">
              <label>Password</label>
              {!isSignup && (
                <button type="button" className="linklike sm" onClick={() => void sendRecoveryCode()} disabled={busy !== null}>
                  {busy === 'forgot' ? 'Sending…' : 'Forgot password?'}
                </button>
              )}
            </div>
            <input type="password" autoComplete={isSignup ? 'new-password' : 'current-password'} value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={isSignup ? 8 : undefined} />
          </div>

          <button type="submit" className="btn primary block lift" disabled={busy !== null}>
            {busy === 'password' ? (isSignup ? 'Creating account…' : 'Signing in…') : isSignup ? 'Create account' : 'Sign in'}
          </button>
          <button type="button" className="btn ghost sm" onClick={() => void sendSignInCode()} disabled={busy !== null}>
            {busy === 'code' ? 'Sending…' : 'Email me a sign-in code instead'}
          </button>

          {(oauthProviders.length > 0 || ssoEnabled) && <div className="auth-divider">or {isSignup ? 'sign up' : 'continue'} with</div>}

          <div className="provider-grid">
            {oauthProviders.map((o) => (
              <button key={o.label} type="button" className="provider-btn" onClick={() => void oauth(o.provider, o.label)} disabled={busy !== null}>
                {o.mark && <ProviderMark id={o.mark} />}
                {busy === o.label ? '…' : `Continue with ${o.label}`}
              </button>
            ))}
            {ssoEnabled && (
              <button type="button" className="provider-btn" onClick={() => void sso()} disabled={busy !== null}>
                {busy === 'sso' ? '…' : 'Single sign-on (SSO)'}
              </button>
            )}
          </div>

          {!isSignup && route?.sso && (
            <button type="button" className="btn ghost sm" onClick={() => { setStep('sso'); setError(null); }} disabled={busy !== null}>
              Sign in with SSO instead
            </button>
          )}

          {error && <div className="auth-msg err">⚠ {error}</div>}
          {notice && <div className="auth-msg ok">✓ {notice}</div>}
        </form>

        <p className="auth-alt">
          {isSignup ? 'Already have an account? ' : invite ? 'No account yet? ' : 'New to NovaPSA? '}
          <button type="button" className="linklike" onClick={() => { setMode(isSignup ? 'signin' : 'signup'); if (isSignup && !invite) setStep('email'); setError(null); setNotice(null); }}>
            {isSignup ? 'Sign in' : 'Create one'}
          </button>
        </p>
      </AuthCard>
    </AuthScene>
  );
}
